import Vue from "vue";
import Router from "vue-router";
import { store } from "../store/store";
import RolePermission from "@/views/auth/RolePermission.vue";
import Role from "@/views/auth/Role.vue";
import Permission from "@/views/auth/Permission.vue";
import Attribute from "@/views/attribute/index.vue";
import { permissions as permissionConfig } from "../auth/config";
import advertisementRouter from "./advertisement";
import paymentHistoryRouter from "./paymenthistory";
import approvalRouter from "./approval";
import analytics from "./analytics";
import customerManagement from "./customerManagement";
import projectManager from "./projectManager";
import bulkPromotion from "./bulkPromotion/index.js";
import listOfCriteria from "./bulkNotification";
import approvalNotification from "./approvalNotificationBulk";

// Containers
const DefaultContainer = () => import("@/containers/DefaultContainer.vue");

// Views
const Dashboard = () => import("@/views/Dashboard.vue");
const Home = () => import("@/views/home.vue");

// attribute - Components
const ViewAttribute = () => import("@/views/attribute/ViewAttribute.vue");

// catalog - Components
const CatalogDetails = () => import("@/views/catalog/CatalogDetails.vue");
const Catalog = () => import("@/views/catalog/Catalog.vue");
const Messages = () => import("@/views/messages/messages.vue");
const profile = () => import("@/views/profile/profile.vue");
const viewprofile = () => import("@/views/profile/viewprofile.vue");
const myaccount = () => import("@/views/myaccount/myaccount.vue");
// Item - Components
const BulkCreateItem = () => import("@/views/item/BulkCreateItem.vue");
const CreateItem = () => import("@/views/item/CreateItem.vue");
const EditItem = () => import("@/views/item/EditItem.vue");
const CopyItem = () => import("@/views/item/CopyItem.vue");
const ViewItem = () => import("@/views/item/ViewItem.vue");
const Item = () => import("@/views/item/Item.vue");

// Login
const Login = () => import("@/views/authentication/Login.vue");
const RecoverPassword = () =>
  import("@/views/authentication/RecoverPassword.vue");
const ChangePassword = () =>
  import("@/views/authentication/ForceChangePassword.vue");
const ConfirmMail = () => import("@/views/authentication/ConfirmMail.vue");

// Users
const Users = () => import("@/views/users/Users.vue");
const User = () => import("@/views/users/User.vue");

// FAQ
const FAQ = () => import("@/views/faq/FAQ.vue");

// Master setup
const SetupComponent = () => import("@/views/setup/Setup.vue");
const Faq = () => import("@/views/setup/Faq.vue");
const Taxcode = () => import("@/views/setup/Taxcode.vue");
const concern = () => import("@/views/setup/concern.vue");
const Resources = () => import("@/views/setup/currency/Resources.vue");
const Servicerequest = () => import("@/views/setup/ServiceRequest.vue");
const Coupon = () => import("@/views/setup/Coupon.vue");
const subscriptionplan = () => import("@/views/setup/subscriptionplan.vue");
const accessrule = () => import("@/views/setup/accessrule.vue");
const deliverysetup = () => import("@/views/setup/deliverysetup.vue");
const SetupCommission = () =>
  import("@/views/setup/commission/CommissionCreate.vue");
const SetupEmailTemplate = () =>
  import("@/views/notificationTemplate/createTemplate.vue");

// Customer
const CustomerComponent = () => import("@/views/customer/Customer.vue");

Vue.use(Router);

const TYPE = "ADMIN";

export default new Router({
  mode: "hash",
  linkActiveClass: "open active",
  routes: [
    {
      path: "/",
      name: "Home",
      meta: {
        requiresVisitor: true,
      },
      beforeEnter: (to, from, next) => {
        let user = JSON.parse(sessionStorage.getItem("userLogin") as string);
        if (
          store.getters["auth/getUserID"] ||
          (user && Object.keys(user).length > 0)
        ) {
          next();
        } else {
          next("Login");
        }
      },
      component: DefaultContainer,
      children: [
        {
          path: "home",
          component: Home,
          meta: {
            requiresAuth: true,
          },
        },
        {
          path: "dashboard",
          name: "Dashboard",
          component: Dashboard,
          meta: {
            requiresAuth: true,
            type: TYPE,
          },
        },
        {
          path: "users",
          meta: { label: "Users" },
          component: {
            render(c) {
              return c("router-view");
            },
          },
          children: [
            {
              path: "",
              component: Users,
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
            },
            {
              path: ":id",
              meta: {
                label: "User Details",
                requiresAuth: true,
                type: TYPE,
              },
              name: "User",
              component: User,
            },
          ],
        },
        {
          path: "role",
          meta: { label: "Role" },
          component: Role,
          props: {
            auth: permissionConfig.role,
          },
        },
        {
          path: "permission",
          meta: { label: "Permission" },
          component: Permission,
          props: {
            auth: permissionConfig.permission,
          },
        },
        {
          path: "role-permission",
          meta: { label: "Role - Permission Mapping" },
          component: RolePermission,
          name: "RolePermission",
        },
        {
          path: "attribute",
          meta: { label: "Attribute" },
          component: {
            render(c) {
              return c("router-view");
            },
          },
          children: [
            {
              path: "",
              component: ViewAttribute,
              name: "Attribute List",
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
            },
            {
              path: ":id",
              meta: {
                label: "Edit",
                requiresAuth: true,
                type: TYPE,
              },
              name: "AttributeForm",
              component: Attribute,
            },
            {
              path: ":id",
              meta: {
                label: "View",
                requiresAuth: true,
                type: TYPE,
              },
              name: "ViewAttribute",
              component: Attribute,
            },
            {
              path: "create",
              name: "CreateAttribute",
              meta: {
                label: "Create Attribute",
                requiresAuth: true,
                type: TYPE,
              },
              component: Attribute,
            },
          ],
        },
        {
          path: "item",
          meta: { label: "Item List" },
          component: {
            render(c) {
              return c("router-view");
            },
          },
          children: [
            {
              path: "",
              component: Item,
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
            },
            {
              path: "partners/:id",
              name: "Item",
              component: Item,
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
            },
            {
              path: ":id?",
              meta: {
                label: "Edit",
                requiresAuth: true,
                type: TYPE,
              },
              name: "Item",
              component: CreateItem,
              props: {
                context: "edit",
              },
            },
            {
              path: "edit/:id",
              meta: {
                label: "Edit Item",
                requiresAuth: true,
                type: TYPE,
              },
              name: "EditItem",
              component: EditItem,
              props: {
                context: "edit",
              },
            },
            {
              path: "copy/:id",
              meta: {
                label: "Copy Item",
                requiresAuth: true,
                type: TYPE,
              },
              name: "CopyItem",
              component: CopyItem,
            },
            {
              path: "view/:id",
              meta: {
                label: "View",
                requiresAuth: true,
                type: TYPE,
              },
              name: "ViewItem",
              component: ViewItem,
            },

            {
              path: "create",
              component: {
                render(c) {
                  return c("router-view");
                },
              },
              children: [
                {
                  path: ":id",
                  name: "CreateItem",
                  meta: {
                    label: "Create Item",
                    requiresAuth: true,
                    type: TYPE,
                  },
                  component: CreateItem,
                },
              ],
            },
            {
              path: "bulk",
              component: {
                render(c) {
                  return c("router-view");
                },
              },
              children: [
                {
                  path: "create/:id",
                  meta: {
                    label: "Bulk Create",
                    requiresAuth: true,
                    type: TYPE,
                  },
                  name: "Bulkcreate",
                  component: BulkCreateItem,
                },
              ],
            },
          ],
        },
        {
          path: "catalog",
          meta: { label: "Catalog" },
          component: {
            render(c) {
              return c("router-view");
            },
          },
          children: [
            {
              path: "create",
              name: "CatalogCreate",
              meta: {
                label: "Create Catalog",
                requiresAuth: true,
                type: TYPE,
              },
              component: CatalogDetails,
            },
            {
              path: "",
              component: Catalog,
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
            },
            {
              path: "view-catalog/:id",
              name: "CatalogDetails",
              meta: {
                label: "Catalog Details",
              },
              component: CatalogDetails,
              props: {
                edit: true,
              },
            },
          ],
        },
        {
          path: "partner",
          meta: { label: "Partner" },
          name: "PartnerList",
          component: {
            render(c) {
              return c("router-view");
            },
          },
          children: [
            {
              path: "/",
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
              component: () => import("@/views/partner/Partner.vue"),
            },
            {
              path: "create",
              name: "CreatePartner",
              meta: {
                label: "Create Partner",
                requiresAuth: true,
                type: TYPE,
              },
              component: () => import("@/views/partner/Create.vue"),
            },

            {
              path: "",
              name: "ViewPartner",
              meta: {
                label: "View Partner",
              },
              component: {
                render(c) {
                  return c("router-view");
                },
              },

              children: [
                {
                  path: ":id/view",
                  name: "PartnerView",
                  meta: {
                    requiresAuth: true,
                    type: TYPE,
                  },
                  component: () => import("@/views/partner/View.vue"),
                },
                {
                  path: "partners/:id",
                  meta: { label: "My Partners" },
                  name: "ChildPartnerList",
                  component: () => import("@/views/partner/Partner.vue"),
                },
                {
                  path: ":id/subcription-assign",
                  name: "Create Subscription",
                  meta: {
                    label: "Create Subscription",
                    requiresAuth: true,
                    type: TYPE,
                  },
                  component: () =>
                    import("@/views/subscriptions/CreateSubsription.vue"),
                },
                {
                  path: ":id/payment-info",
                  name: "Payment Info",
                  meta: {
                    label: "Payment Info",
                    requiresAuth: true,
                    type: TYPE,
                  },
                  component: () =>
                    import(
                      "@/views/paymentinfo/partnerPaymentInfoByTenant.vue"
                    ),
                },
                {
                  path: ":id/view-subscription",
                  name: "view-subscription",
                  meta: {
                    label: "View Subscription",
                    requiresAuth: true,
                    type: TYPE,
                  },
                  component: () =>
                    import("@/views/subscriptions/ViewSubscription.vue"),
                },

                {
                  path: ":id/view-Commission",

                  meta: {
                    label: "view Commission",
                    requiresAuth: true,
                    type: TYPE,
                  },
                  name: "View Commission",
                  component: () =>
                    import("@/views/commission/CommissionAssign.vue"),
                },

                {
                  path: ":id/Create-Commission",
                  meta: {
                    label: "Create Commission",
                    requiresAuth: true,
                    type: TYPE,
                  },
                  name: "Create Commission",
                  component: () =>
                    import("@/views/commission/createCommissionAssign.vue"),
                },

                {
                  path: ":id/wallet-statement",
                  name: "Wallet Statement",
                  meta: {
                    label: "Wallet Statement",
                    requiresAuth: true,
                    type: TYPE,
                  },
                  component: () => import("@/views/wallet/WalletStatement.vue"),
                },
                {
                  path: ":id/delivery-preference",
                  meta: {
                    label: "Delivery Preference",
                    requiresAuth: true,
                    type: TYPE,
                  },
                  name: "Delivery Preference",
                  component: () =>
                    import(
                      "@/components/MasterSetup/deliverysetup/DeliveryPreference/DeliveryPreference.vue"
                    ),
                },
                {
                  path: ":id/promotion-preference",
                  meta: {
                    label: "Promotion Preference",
                    requiresAuth: true,
                    type: TYPE,
                  },
                  name: "Promotion Preference",
                  component: () =>
                    import(
                      "@/components/MasterSetup/Coupon/promotionPreference/promotionPreference.vue"
                    ),
                },
                {
                  path: "order-preference",
                  meta: {
                    label: "Order Preference",
                    requiresAuth: true,
                    type: TYPE,
                  },
                  component: () => import("@/views/orders/OrderPreference.vue"),
                },

                {
                  path: ":id/view-invoice",

                  meta: {
                    label: "View Invoice",
                    requiresAuth: true,
                    type: TYPE,
                  },
                  component: {
                    render(c) {
                      return c("router-view");
                    },
                  },

                  children: [
                    {
                      path: "",
                      name: "View Invoice",
                      meta: {
                        requiresAuth: true,
                        type: TYPE,
                      },
                      component: () =>
                        import("@/views/invoice/InvoiceHistory.vue"),
                    },
                    {
                      path: "details",
                      meta: {
                        label: "Details",
                        requiresAuth: true,
                        type: TYPE,
                      },
                      component: () =>
                        import("@/views/invoice/InvoiceDetails.vue"),
                    },
                    {
                      path: "unbilled",
                      meta: {
                        label: "Unbilled",
                        requiresAuth: true,
                        type: TYPE,
                      },
                      component: () =>
                        import(
                          "@/views/invoice/partnerUnbilledInvoiceByTenant.vue"
                        ),
                    },
                  ],
                },
              ],
            },
          ],
        },

        {
          path: "customer",
          meta: { label: "Customer" },
          name: "CustomerList",
          component: {
            render(c) {
              return c("router-view");
            },
          },
          children: [
            {
              path: "",
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
              component: CustomerComponent,
            },
          ],
        },
        {
          path: "tenant",
          meta: { label: "Tenant" },
          component: {
            render(c) {
              return c("router-view");
            },
          },
          children: [
            {
              path: "",
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
              component: () => import("@/views/tenant/Tenant.vue"),
            },
            {
              path: "create",
              name: "CreateTenant",
              meta: {
                label: "Create Tenant",
                requiresAuth: true,
                type: TYPE,
              },
              component: () => import("@/views/tenant/CreateTenant.vue"),
            },
          ],
        },
        {
          path: "user",
          meta: { label: "User Management" },
          name: "User",
          component: {
            render(c) {
              return c("router-view");
            },
          },
          children: [
            {
              path: "",
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
              component: () => import("@/views/tenant/User.vue"),
            },
            {
              path: "partners/:id",
              name: "User Management",
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
              component: () => import("@/views/tenant/User.vue"),
            },
            {
              path: "/user/create",
              name: "CreateUser",
              meta: {
                label: "Create User",
                requiresAuth: true,
                type: TYPE,
              },
              component: () => import("@/views/tenant/CreateUser.vue"),
            },
            {
              path: "/user/view/:id",
              name: "ViewUser",
              meta: {
                label: "View User",
                requiresAuth: true,
                type: TYPE,
              },
              component: () => import("@/views/tenant/ViewUser.vue"),
            },
            {
              path: "/user/Edit",
              name: "EditUser",
              meta: {
                label: "Edit User",
                requiresAuth: true,
                type: TYPE,
              },
              component: () => import("@/views/tenant/EditUser.vue"),
            },
            {
              path: "/user/Kyc/create",
              name: "CreateKyc",
              meta: {
                label: "Create Kyc",
                requiresAuth: true,
                type: TYPE,
              },
              component: () => import("@/views/tenant/kycCreate.vue"),
            },
            {
              path: "/user/Kyc/update",
              name: "UpdateKyc",
              meta: {
                label: "Update Kyc",
                requiresAuth: true,
                type: TYPE,
              },
              component: () => import("@/views/tenant/kycUpdate.vue"),
            },
          ],
        },
        {
          path: "inventory",
          meta: { label: "Inventory" },
          component: {
            render(c) {
              return c("router-view");
            },
          },
          children: [
            {
              path: "",
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
              component: () => import("@/views/inventory/Inventory.vue"),
            },
            {
              path: "/inventory/viewStockDetails",
              name: "viewStockDetails",
              meta: {
                label: "View Stock Details",
                requiresAuth: true,
                type: TYPE,
              },
              component: () => import("@/views/inventory/viewStockDetails.vue"),
            },
          ],
        },

        {
          path: "commission",
          meta: { label: "Commission" },
          component: {
            render(c) {
              return c("router-view");
            },
          },
          children: [
            {
              path: "",
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
              component: () => import("@/views/commission/Commission.vue"),
            },
          ],
        },

        {
          path: "offerimage",
          meta: { label: "offerimage" },
          component: {
            render(c) {
              return c("router-view");
            },
          },
          children: [
            {
              path: "",
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
              component: () => import("@/views/Offerimage/offerimage.vue"),
            },
          ],
        },

        {
          path: "office-management",
          meta: { label: "Office Management" },
          component: {
            render(c) {
              return c("router-view");
            },
          },
          children: [
            {
              path: "",
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
              component: () => import("@/views/office/Office.vue"),
            },
            {
              path: "create",
              name: "CreateOffice",
              meta: {
                label: "Create Office",
                requiresAuth: true,
                type: TYPE,
              },
              component: () => import("@/views/office/CreateOffice.vue"),
            },
            {
              path: "view",
              name: "ViewOffice",
              meta: {
                label: "View Office",
                requiresAuth: true,
                type: TYPE,
              },
              component: () => import("@/views/office/ViewOffice.vue"),
            },
          ],
        },
        {
          path: "suboffice-management",
          meta: { label: "Stock Point Management" },
          component: {
            render(c) {
              return c("router-view");
            },
          },
          children: [
            {
              path: "",
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
              component: () => import("@/views/office/SubOffice.vue"),
            },
            {
              path: "create",
              name: "CreateSubOffice",
              meta: {
                label: "Create Stock Point",
                requiresAuth: true,
                type: TYPE,
              },
              component: () => import("@/views/office/CreateSubOffice.vue"),
            },
            {
              path: "view",
              name: "ViewSubOffice",
              meta: {
                label: "View Stock Point",
                requiresAuth: true,
                type: TYPE,
              },
              component: () => import("@/views/office/ViewSubOffice.vue"),
            },
          ],
        },
        {
          path: "manage-orders",
          meta: { label: "Manage Orders" },
          component: {
            render(c) {
              return c("router-view");
            },
          },
          children: [
            {
              path: "",
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
              component: () => import("@/views/orders/ManageOrder.vue"),
            },
            {
              path: "partners/:id",
              name: "Manage-Orders",
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
              component: () => import("@/views/orders/ManageOrder.vue"),
            },
            {
              path: "/order/:id",
              name: "OrderDetail",
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
              component: () => import("@/views/orders/OrderDetails.vue"),
            },
            {
              path: "/order/:id/order-invoice",
              name: "Order-Invoice",
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
              component: () => import("@/views/orders/OrderInvoice.vue"),
            },
          ],
        },
        {
          path: "manage_own_customer",
          meta: { label: "Manage Own Customer" },
          component: {
            render(c) {
              return c("router-view");
            },
          },
          children: [
            {
              path: "",
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
              component: () =>
                import("@/views/manageOwnCustomer/ManageOwnCustomer.vue"),
            },
            {
              path: "/manage_own_customer/customerDetails",
              name: "Customer-Details",
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
              component: () =>
                import("@/views/manageOwnCustomer/customerDetails.vue"),
            },
            {
              path: "/manage_own_customer/uploadHistory",
              name: "Upload-History",
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
              component: () =>
                import("@/views/manageOwnCustomer/uploadHistory.vue"),
            },
            {
              path: "/manage_own_customer/create",
              name: "Create New Customer",
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
              component: () =>
                import("@/views/manageOwnCustomer/createCustomer.vue"),
            },
            {
              path: "/manage_own_customer/bulkUpload",
              name: "Bulk Upload",
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
              component: () =>
                import("@/views/manageOwnCustomer/bulkUpload.vue"),
            },
            {
              path: "/manage_own_customer/detail",
              name: "DetailView",
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
              component: () =>
                import("@/views/manageOwnCustomer/DetailView.vue"),
            },
            {
              path: "/manage_own_customer/edit",
              name: "editCustomer",
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
              component: () =>
                import("@/views/manageOwnCustomer/editCustomer.vue"),
            },
          ],
        },
        {
          path: "serviceticket",
          meta: {
            label: "Service Ticket",
            requiresAuth: true,
            type: TYPE,
          },
          component: {
            render(c) {
              return c("router-view");
            },
          },
          children: [
            {
              path: "open",
              meta: {
                label: "Open",
                requiresAuth: true,
                type: TYPE,
              },
              component: {
                render(c) {
                  return c("router-view");
                },
              },
              children: [
                {
                  path: "",
                  component: () =>
                    import(
                      "@/views/serviceticket/customertickets/CustomerOpenTickets.vue"
                    ),
                },
                {
                  path: "edit/:id",
                  name: "Edit",
                  meta: {
                    label: "Edit",
                    requiresAuth: true,
                    type: TYPE,
                  },
                  component: () =>
                    import(
                      "@/views/serviceticket/customertickets/CustomerTicketEdit.vue"
                    ),
                },
              ],
            },
            {
              path: "close",
              meta: { label: "Close", requiresAuth: true, type: TYPE },
              component: {
                render(c) {
                  return c("router-view");
                },
              },
              children: [
                {
                  path: "",

                  component: () =>
                    import(
                      "@/views/serviceticket/customertickets/CustomerClosedTickets.vue"
                    ),
                },
                {
                  path: "view/:id",
                  name: "View",
                  meta: {
                    label: "View",
                    requiresAuth: true,
                    type: TYPE,
                  },
                  component: () =>
                    import(
                      "@/views/serviceticket/customertickets/CustomerClosedTicketView.vue"
                    ),
                },
              ],
            },
          ],
        },
        {
          path: "discountcoupon",
          meta: { label: "Discount & Counts" },
          component: {
            render(c) {
              return c("router-view");
            },
          },
          children: [
            {
              path: "",
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
              component: () => import("@/views/discounts/Discounts.vue"),
            },
          ],
        },

        {
          path: "manage-profile",
          meta: { label: "Manage - Profile" },
          component: {
            render(c) {
              return c("router-view");
            },
          },
          children: [
            {
              path: "",
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
              component: () => import("@/views/profile/profile.vue"),
            },
          ],
        },

        {
          path: "promotions",
          meta: { label: "Promotions" },
          component: {
            render(c) {
              return c("router-view");
            },
          },
          children: [
            {
              path: "",
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
              component: () => import("@/views/promotion/Promotion.vue"),
            },
          ],
        },
        {
          path: "messages",
          meta: { label: "Messages" },
          component: {
            render(c) {
              return c("router-view");
            },
          },
          children: [
            {
              path: "",
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
              component: () => import("@/views/messages/messages.vue"),
            },
          ],
        },
        {
          path: "myaccount",
          meta: { label: "My Account" },
          component: {
            render(c) {
              return c("router-view");
            },
          },
          children: [
            {
              path: "",
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
              component: () => import("@/views/myaccount/myaccount.vue"),
            },
            {
              path: "changepassword",
              meta: { label: "Chnage  Password" },
              component: {
                render(c) {
                  return c("router-view");
                },
              },
              children: [
                {
                  path: "",
                  meta: {
                    requiresAuth: true,
                    type: TYPE,
                  },
                  component: () => import("@/views/profile/changepassword.vue"),
                },
              ],
            },
            {
              path: "walletstatement",
              meta: { label: "Wallet Statement" },
              component: {
                render(c) {
                  return c("router-view");
                },
              },
              children: [
                {
                  path: "",
                  meta: {
                    requiresAuth: true,
                    type: TYPE,
                  },
                  component: () =>
                    import("@/views/wallet/WalletStatementPartnerOwn.vue"),
                },
              ],
            },
            {
              path: "delivery-preference",
              meta: { label: "Delivery Preference" },
              component: {
                render(c) {
                  return c("router-view");
                },
              },
              children: [
                {
                  path: "",
                  meta: {
                    requiresAuth: true,
                    type: TYPE,
                  },
                  component: () =>
                    import(
                      "@/components/MasterSetup/deliverysetup/DeliveryPreference/DeliveryPreference.vue"
                    ),
                },
              ],
            },
            {
              path: "order-preference",
              meta: { label: "Order Preference" },
              component: {
                render(c) {
                  return c("router-view");
                },
              },
              children: [
                {
                  path: "",
                  meta: {
                    requiresAuth: true,
                    type: TYPE,
                  },
                  component: () => import("@/views/orders/OrderPreference.vue"),
                },
              ],
            },
            {
              path: "my-invoice",
              meta: { label: "My Invoice" },
              component: {
                render(c) {
                  return c("router-view");
                },
              },
              children: [
                {
                  path: "",
                  meta: {
                    requiresAuth: true,
                    type: TYPE,
                  },
                  component: () =>
                    import("@/views/invoice/myPartnerInvoice.vue"),
                },
                {
                  path: "details",
                  meta: {
                    label: "Details",
                    requiresAuth: true,
                    type: TYPE,
                  },
                  component: () =>
                    import("@/views/invoice/myInvoiceDetails.vue"),
                },
                {
                  path: "unbilled",
                  meta: {
                    label: "Unbilled Invoice",
                    requiresAuth: true,
                    type: TYPE,
                  },
                  component: () =>
                    import("@/views/invoice/partnerUnbilledInvoice.vue"),
                },
              ],
            },
            {
              path: "Paymentinfo",
              meta: { label: "Payment-Info" },
              component: {
                render(c) {
                  return c("router-view");
                },
              },
              children: [
                {
                  path: "",
                  meta: {
                    requiresAuth: true,
                    type: TYPE,
                  },
                  component: () =>
                    import("@/views/paymentinfo/paymentinfo.vue"),
                },
              ],
            },
            {
              path: "subscriptionseller",
              meta: { label: "Subsciption" },
              component: {
                render(c) {
                  return c("router-view");
                },
              },
              children: [
                {
                  path: "",
                  meta: {
                    requiresAuth: true,
                    type: TYPE,
                  },
                  component: () =>
                    import("@/views/subscriptionseller/subscriptionseller.vue"),
                },
              ],
            },
            {
              path: "viewprofile",
              meta: { label: " View-profile" },
              component: {
                render(c) {
                  return c("router-view");
                },
              },
              children: [
                {
                  path: "",
                  meta: {
                    requiresAuth: true,
                    type: TYPE,
                  },
                  component: () => import("@/views/profile/viewprofile.vue"),
                },
                {
                  path: "edit-profile",
                  meta: { label: "Edit profile" },
                  component: {
                    render(c) {
                      return c("router-view");
                    },
                  },
                  children: [
                    {
                      path: "",
                      meta: {
                        requiresAuth: true,
                        type: TYPE,
                      },
                      component: () => import("@/views/profile/profile.vue"),
                    },
                  ],
                },
              ],
            },
          ],
        },
        {
          path: "subscriptionplan",
          meta: { label: "Pubscription-Plan" },
          component: {
            render(c) {
              return c("router-view");
            },
          },
          children: [
            {
              path: "",
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
              component: () =>
                import("@/views/subscriptionplan/subscriptionplan.vue"),
            },
          ],
        },

        {
          path: "faqdetails",
          meta: { label: "faqdetails" },
          component: {
            render(c) {
              return c("router-view");
            },
          },
          children: [
            {
              path: "",
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
              component: () => import("@/views/faqdetails/faqdetails.vue"),
            },
          ],
        },
        {
          path: "manage-team",
          meta: { label: "Manage Team" },
          component: {
            render(c) {
              return c("router-view");
            },
          },
          children: [
            {
              path: "",
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
              component: () => import("@/views/team/Team.vue"),
            },
          ],
        },
        {
          path: "faq",
          meta: { label: "View FAQ" },
          component: {
            render(c) {
              return c("router-view");
            },
          },
          children: [
            {
              path: "",
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
              component: FAQ,
            },
          ],
        },
        {
          path: "notification-template",
          name: "Notification Template",
          meta: {
            requiresAuth: true,
            type: TYPE,
          },
          component: {
            render(c) {
              return c("router-view");
            },
          },
          children: [
            {
              path: "create",
              name: "Create",
              meta: {
                label: "Create",
                requiresAuth: true,
                type: TYPE,
              },
              component: SetupEmailTemplate,
            },
            {
              path: "edit/:id",
              name: "templateSetupEdit",
              meta: {
                label: "Edit",
                requiresAuth: true,
                type: TYPE,
              },
              component: () =>
                import("@/views/notificationTemplate/templateEdit.vue"),
            },
            {
              path: "",
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
              component: () =>
                import("@/views/notificationTemplate/templateList.vue"),
            },
          ],
        },
        {
          path: "setup",
          meta: { label: "Setup" },
          component: {
            render(c) {
              return c("router-view");
            },
          },
          children: [
            /*{
              path: "",
              meta: {
                requiresAuth: true,
                type: TYPE
              },
              component: SetupComponent
            },*/
            {
              path: "Resources",
              name: "Resources",
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
              component: {
                render(c) {
                  return c("router-view");
                },
              },
              children: [
                {
                  path: "",
                  meta: {
                    requiresAuth: true,
                    type: TYPE,
                  },
                  component: () =>
                    import("@/views/setup/currency/Resources.vue"),
                },
                {
                  path: "currencyViewDetail/:id",
                  name: "currencyViewDetail",
                  meta: {
                    label: "View",
                    requiresAuth: true,
                    type: TYPE,
                  },
                  component: () =>
                    import("@/views/setup/currency/currencyViewDetail.vue"),
                },
                {
                  path: "editCurrency/:id",
                  name: "editCurrency",
                  meta: {
                    label: "Edit Currency",
                    requiresAuth: true,
                    type: TYPE,
                  },
                  component: () =>
                    import("@/views/setup/currency/editCurrency.vue"),
                },
                {
                  path: "addCurrency",
                  name: "addCurrency",
                  meta: {
                    label: "Add Currency",
                    requiresAuth: true,
                    type: TYPE,
                  },
                  component: () =>
                    import("@/views/setup/currency/addCurrency.vue"),
                },
              ],
            },
            {
              path: "concern",
              name: "concern",
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
              component: concern,
            },
            {
              path: "faq",
              name: "Faqs",
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
              component: Faq,
            },
            {
              path: "taxcode",
              name: "Taxcode",
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
              component: Taxcode,
            },
            {
              path: "coupon",
              name: "Coupon",
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
              component: Coupon,
            },
            {
              path: "subscriptionplan",
              name: "Subscription-plan",
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
              component: subscriptionplan,
            },
            {
              path: "accessrule",
              name: "Access Rule",
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
              component: accessrule,
            },
            {
              path: "deliverysetup",
              name: "Delivery Setup",
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
              component: deliverysetup,
            },
            {
              path: "service-request",
              name: "ServiceRequest",
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
              component: Servicerequest,
            },
            {
              path: "commission",
              name: "Commission Setup",
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
              component: {
                render(c) {
                  return c("router-view");
                },
              },
              children: [
                {
                  path: "",
                  meta: {
                    requiresAuth: true,
                    type: TYPE,
                  },
                  component: () =>
                    import("@/views/setup/commission/CommissionRuleList.vue"),
                },
                {
                  path: "create",
                  name: "Create",
                  meta: {
                    label: "Create",
                    requiresAuth: true,
                    type: TYPE,
                  },
                  component: SetupCommission,
                },
                {
                  path: "edit/:id",
                  name: "CommissionSetupEdit",
                  meta: {
                    label: "Edit",
                    requiresAuth: true,
                    type: TYPE,
                  },
                  component: () =>
                    import("@/views/setup/commission/CommissionEdit.vue"),
                },
              ],
            },
          ],
        },
        {
          path: "service-request",
          meta: { label: "Service Request" },
          component: {
            render(c) {
              return c("router-view");
            },
          },
          children: [
            {
              path: "",
              meta: {
                requiresAuth: true,
                type: TYPE,
              },
              component: () =>
                import("@/views/serviceRequest/ServiceRequest.vue"),
            },
          ],
        },
        advertisementRouter,
        paymentHistoryRouter,
        approvalRouter,
        analytics,
        customerManagement,
        projectManager,
        bulkPromotion,
        listOfCriteria,
        approvalNotification,
      ],
    },
    {
      path: "/login",
      name: "Login",
      component: Login,
      meta: {
        requiresVisitor: true,
      },
    },
    {
      path: "/ConfirmMail",
      name: "confirm-mail",
      component: ConfirmMail,
    },
    {
      path: "/recoverpw",
      name: "recoverpw",
      component: RecoverPassword,
    },

    {
      path: "/changepassword",
      name: "changepassword",
      component: ChangePassword,
    },
    {
      path: "/reset-password-email",
      name: "resetpasswordemail",
      component: () => import("@/views/authentication/VerificationOtp.vue"),
    },
  ],
});
